import { QUERY_KEYS } from "@/libraries/constant";
import type { CreateChannelBodyPayloadSchema } from "@/schemas";
import { channelService } from "@/services";
import type { ContactMemberDetailResponse } from "@/types";
import type { QueryClient } from "@tanstack/react-query";
import { merge } from "lodash";

const createChannel = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CREATE_CHANNEL], {
		mutationFn: (payload: CreateChannelBodyPayloadSchema) =>
			channelService.create(payload),
		retry: 2,
		onMutate: async (newChannel) => {
			const qKey = [QUERY_KEYS.GET_MEMBER, +newChannel.messageable_id];
			await queryClient.cancelQueries({
				queryKey: qKey,
			});
			const oldMember =
				queryClient.getQueryData<ContactMemberDetailResponse>(qKey);
			if (!oldMember) return;
			const optimisticMember: ContactMemberDetailResponse = {
				...oldMember,
				user: {
					...oldMember.user,
					channel_name: newChannel.name,
				},
			};
			queryClient.setQueryData<ContactMemberDetailResponse>(
				qKey,
				(oldMemberData) => ({
					...oldMemberData,
					user: merge(oldMemberData?.user ?? {}, optimisticMember.user),
				}),
			);
			return { memberId: optimisticMember.user.id, oldMember };
		},
		onError: async (_err, _variables, ctx) => {
			if (ctx?.memberId && ctx?.oldMember) {
				queryClient.setQueryData<ContactMemberDetailResponse>(
					[QUERY_KEYS.GET_MEMBER, ctx.memberId],
					ctx.oldMember,
				);
			}
		},
	});
};

export const channelMutations = (queryClient: QueryClient) => {
	createChannel(queryClient);
};
